import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

/**
 * Hook to get query data from React Query cache
 *
 * Multiple approaches:
 * 1. getQueryData - Gets data for an EXACT query key match
 * 2. getQueriesData - Gets ALL queries that match the filters (including partial matches)
 * 3. getLatestQueriesData - Gets the most recently updated queries
 * 4. getFreshQueriesData - Gets queries that are considered "fresh" based on staleTime
 */
const useGetAllCacheDataWithQueryKey = () => {
  const queryClient = useQueryClient();

  /**
   * Get data for a single query with exact key match
   * Example: getQueryData(['approving-invoice-reports'])
   */
  const getExactQueryData = useCallback(
    <T = unknown>(queryKey: unknown[]) => {
      return queryClient.getQueryData<T>(queryKey);
    },
    [queryClient]
  );

  /**
   * Get ALL queries that match the query key (including partial matches)
   * Example: getQueriesData({ queryKey: ['approving-invoice-reports'] })
   * This will return ALL queries like:
   * - ['approving-invoice-reports']
   * - ['approving-invoice-reports', { page: 1 }]
   * - ['approving-invoice-reports', { page: 2, status: 'pending' }]
   * etc.
   *
   * Returns an array of [queryKey, data] tuples
   */
  const getAllQueriesData = useCallback(
    <T = unknown>(queryKey: unknown[]) => {
      return queryClient.getQueriesData<T>({ queryKey });
    },
    [queryClient]
  );

  /**
   * Get all queries data and merge them into a single array
   * Useful when you want to combine data from multiple queries
   */
  const getAllQueriesDataMerged = useCallback(
    <T = unknown>(queryKey: unknown[]) => {
      const allQueries = queryClient.getQueriesData<T[]>({ queryKey });

      // Merge all data arrays into one
      const mergedData = allQueries.reduce<T[]>((acc, [, data]) => {
        if (Array.isArray(data)) {
          return [...acc, ...data];
        }
        return acc;
      }, []);

      return mergedData;
    },
    [queryClient]
  );

  return {
    getExactQueryData,
    getAllQueriesData,
    getAllQueriesDataMerged,
  };
};

export { useGetAllCacheDataWithQueryKey };
